import { BehaviorSubject, noop } from 'rxjs';
import { BaseService, NULL_SERVICE, Service } from '../base/service';

export interface CommitService extends Service {
  readonly isCommitting: boolean;
  commitAsync(): Promise<void>;
  /** Callback-style variation in case the implicit microtask is undesirable. */
  commit(onDone: () => void): void;
}

export const NULL_COMMIT_SERVICE: CommitService = {
  ...NULL_SERVICE,
  isCommitting: false,
  commitAsync: () => Promise.resolve(),
  commit: noop,
};

type CommitProcessingWaiter = (onDone: () => void) => void;

const defaultCommitProcessingWaiter: CommitProcessingWaiter = onDone => {
  onDone();
};

export function createSimpleCommitService(waitForProcessing = defaultCommitProcessingWaiter): CommitService {
  return new SimpleCommitService(waitForProcessing);
}

export class SimpleCommitService extends BaseService implements CommitService {
  constructor(waitForProcessing: CommitProcessingWaiter) {
    const isCommitting$ = new BehaviorSubject(false);

    super({
      changes$: isCommitting$,
    });

    this.isCommitting$ = isCommitting$;
    this.waitForProcessing = waitForProcessing;
  }

  protected readonly isCommitting$: BehaviorSubject<boolean>;
  protected readonly waitForProcessing: CommitProcessingWaiter;

  get isCommitting() {
    return this.isCommitting$.getValue();
  }

  commitAsync(): Promise<void> {
    return new Promise(resolve => {
      this.commit(resolve);
    });
  }

  commit(onDone: () => void) {
    this.isCommitting$.next(true);

    this.waitForProcessing(() => {
      try {
        onDone();
      } finally {
        this.isCommitting$.next(false);
      }
    });
  }
}
